/**
 * ConfidenceMeter — the overall / per-line confidence bar with the auto-accept
 * threshold marker and an optional % readout.
 *
 * Built on the DS `Meter` (a measurement, not task progress → `Meter`, not
 * `Progress`). The tone bands the value (success / warning / danger); the marker
 * is a token-coloured tick at the fixed auto-accept threshold — above it a match
 * is touchless-eligible. The threshold is a single app-wide policy constant, so
 * its position is a static token utility (no inline style, no runtime px).
 */

import { cn, Meter, Typography } from "@norican/react";

/** Confidence at/above which a match is auto-accept (touchless) eligible. Percent. */
export const AUTO_ACCEPT_THRESHOLD = 88;

type MeterTone = "success" | "warning" | "danger";

function toneFor(value: number): MeterTone {
  if (value >= 85) {
    return "success";
  }
  if (value >= 60) {
    return "warning";
  }
  return "danger";
}

export interface ConfidenceMeterProps {
  /** Confidence on 0–100. */
  value: number;
  /** Render the trailing % readout. Defaults to true. */
  showValue?: boolean;
  /** Track size. Defaults to `sm`. */
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function ConfidenceMeter({
  value,
  showValue = true,
  size = "sm",
  className,
}: ConfidenceMeterProps) {
  const tone = toneFor(value);
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Meter.Root value={value} tone={tone} size={size} className="min-w-16 flex-1">
        <Meter.Track className="relative">
          <Meter.Indicator />
          {/* Auto-accept threshold marker at AUTO_ACCEPT_THRESHOLD %. */}
          <span
            aria-hidden
            className="bg-fg-subtle absolute inset-y-0 left-[88%] w-0.5 rounded-full opacity-70"
          />
        </Meter.Track>
      </Meter.Root>
      {showValue && (
        <Typography variant="caption" tabular className="text-fg w-8 shrink-0 text-right">
          {value}%
        </Typography>
      )}
    </div>
  );
}
